/**
 * Beacon ticket manager for the add-in telemetry pipeline.
 *
 * The `pagehide` flush uses `navigator.sendBeacon`, which cannot set an
 * `Authorization` header. Instead it attaches a short-lived, HMAC-signed ticket
 * (issued by `POST /api/telemetry/v1/beacon-ticket`, scoped to the beacon
 * ingest endpoint) on the request URL. Because `sendBeacon` runs synchronously
 * during teardown there is no time to fetch a ticket then - so this manager
 * fetches one at telemetry init, caches it, and refreshes it on a timer before
 * it lapses. The transport reads the cached value synchronously via
 * {@link BeaconTicketManager.current}.
 *
 * Everything degrades safely: if the endpoint is unreachable, returns 503
 * (beacon ticketing disabled server-side), or the bearer isn't ready yet, the
 * cache stays empty, `current()` returns `null`, and `sendBeacon` simply
 * reports failure so the events remain queued for the next authenticated POST.
 */

/** Timer seam so tests can drive scheduling deterministically. */
export interface BeaconTicketSchedule {
  set: (cb: () => void, ms: number) => unknown;
  clear: (handle: unknown) => void;
}

export interface BeaconTicketManagerOptions {
  /** Absolute URL of the beacon-ticket POST endpoint. */
  endpoint: string;
  /** Resolve the current bearer token (null until sign-in settles). */
  getBearerToken: () => Promise<string | null>;
  /** Override `fetch`. Defaults to `globalThis.fetch`. Test seam. */
  fetchImpl?: typeof fetch;
  /** Timer seam. Defaults to `setTimeout`/`clearTimeout`. */
  schedule?: BeaconTicketSchedule;
  /** Clock. Defaults to `Date.now`. Test seam. */
  now?: () => number;
  /**
   * Refresh this many ms before the ticket's expiry, so a fresh ticket is
   * always cached before the old one lapses. Default 120_000 (2 min).
   */
  refreshBufferMs?: number;
  /** Retry delay (ms) after a failed fetch. Default 30_000 (30 s). */
  retryDelayMs?: number;
}

export interface BeaconTicketManager {
  /** Synchronously read the cached ticket, or `null` if none/expired. */
  current: () => string | null;
  /** Fetch a ticket now and cache it. Never throws. */
  refreshNow: () => Promise<void>;
  /** Kick off the initial fetch and schedule refreshes. Idempotent. */
  start: () => void;
  /** Cancel any pending refresh timer and clear the cached ticket. */
  stop: () => void;
}

const DEFAULT_REFRESH_BUFFER_MS = 120_000;
const DEFAULT_RETRY_DELAY_MS = 30_000;
/** Small safety margin so a ticket about to expire isn't handed out. */
const EXPIRY_MARGIN_MS = 5_000;

/**
 * Create a beacon ticket manager. Call {@link BeaconTicketManager.start} once
 * telemetry is initialised.
 */
export function createBeaconTicketManager(
  options: BeaconTicketManagerOptions,
): BeaconTicketManager {
  if (!options.endpoint) throw new Error("createBeaconTicketManager: endpoint is required");

  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const schedule: BeaconTicketSchedule =
    options.schedule ?? {
      set: (cb, ms) => globalThis.setTimeout(cb, ms),
      clear: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
    };
  const now = options.now ?? (() => Date.now());
  const refreshBufferMs = options.refreshBufferMs ?? DEFAULT_REFRESH_BUFFER_MS;
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

  let ticket: string | null = null;
  let expiresAtMs = 0;
  let timer: unknown;
  let started = false;

  const cancelTimer = (): void => {
    if (timer !== undefined) {
      schedule.clear(timer);
      timer = undefined;
    }
  };

  const scheduleNext = (delayMs: number): void => {
    cancelTimer();
    // Never schedule a negative/zero delay tighter than the retry floor.
    const delay = delayMs > 0 ? delayMs : retryDelayMs;
    timer = schedule.set(() => {
      timer = undefined;
      void refreshNow();
    }, delay);
  };

  const refreshNow = async (): Promise<void> => {
    let bearer: string | null;
    try {
      bearer = await options.getBearerToken();
    } catch {
      bearer = null;
    }

    if (!bearer) {
      // Auth not ready yet - retry shortly. Keep any existing (still-valid)
      // ticket in place meanwhile.
      scheduleNext(retryDelayMs);
      return;
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${bearer}`,
    };

    try {
      const res = await fetchImpl(options.endpoint, { method: "POST", headers });
      if (!res.ok) {
        // 503 = ticketing disabled server-side; any other status = transient.
        scheduleNext(retryDelayMs);
        return;
      }
      const parsed = parseTicket(await res.json());
      if (!parsed) {
        scheduleNext(retryDelayMs);
        return;
      }
      ticket = parsed.ticket;
      expiresAtMs = parsed.expiresAtMs;
      // Refresh a little before expiry; never sooner than the retry floor.
      const lead = expiresAtMs - now() - refreshBufferMs;
      scheduleNext(lead > 0 ? lead : retryDelayMs);
    } catch {
      scheduleNext(retryDelayMs);
    }
  };

  const current = (): string | null => {
    if (!ticket) return null;
    if (now() >= expiresAtMs - EXPIRY_MARGIN_MS) return null;
    return ticket;
  };

  const start = (): void => {
    if (started) return;
    started = true;
    void refreshNow();
  };

  const stop = (): void => {
    cancelTimer();
    ticket = null;
    expiresAtMs = 0;
    started = false;
  };

  return { current, refreshNow, start, stop };
}

/**
 * Parse the beacon-ticket response into `{ ticket, expiresAtMs }`, or `null`
 * when the shape is unusable. Accepts both the standard `{ data: {...} }`
 * envelope and a bare `{ ticket, expiresAtUtc }` object.
 */
function parseTicket(body: unknown): { ticket: string; expiresAtMs: number } | null {
  if (!body || typeof body !== "object") return null;
  const envelope = body as { data?: unknown; ticket?: unknown; expiresAtUtc?: unknown };
  const payload = (envelope.data ?? envelope) as {
    ticket?: unknown;
    expiresAtUtc?: unknown;
  };
  if (typeof payload.ticket !== "string" || !payload.ticket) return null;
  if (typeof payload.expiresAtUtc !== "string") return null;
  const expiresAtMs = Date.parse(payload.expiresAtUtc);
  if (Number.isNaN(expiresAtMs)) return null;
  return { ticket: payload.ticket, expiresAtMs };
}
